import React, { useMemo, useState } from "react"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { faBook, faClose, faFolderOpen, faLink, faVideo } from "@fortawesome/free-solid-svg-icons"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import InputField from "../../../components/InputField/InputField"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { TutorialEntity } from "../../../Objects/Tutorial/TutorialEntity"
import { ETutorialAudienceType } from "../../../Enum/ETutorialAudienceType"
import { useSaveTutorial } from "../../../hooks/Tutorial/useSaveTutorial"
import GetSpace from "../../../utils/space"

interface Props {
  tutorial?: TutorialEntity

  categories: string[]

  onClose: () => void

  onSuccess: () => void
}

export const AdminTutorialPopup: React.FC<Props> = ({ tutorial, categories, onClose, onSuccess }) => {
  const { handleSaveTutorial, handleRemoveTutorial } = useSaveTutorial()

  const [form, setForm] = useState(new TutorialEntity(tutorial ?? {}))

  const filteredCategories = useMemo(() => {
    const normalizedCurrent = form.category.trim().toLocaleLowerCase()

    return categories.filter((item) => {
      const normalizedItem = item.trim().toLocaleLowerCase()

      return normalizedItem.length > 0 && normalizedItem !== normalizedCurrent
    })
  }, [categories, form.category])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    handleSaveTutorial(form, () => {
      onSuccess()
      onClose()
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={tutorial ? "Editar tutorial" : "Novo tutorial"}
              icon={faBook}
              iconColor={THEME.colors.iconBGColor}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  {tutorial && (
                    <>
                      <GenericDropDown
                        data={Object.mapEnumToStrings(EEnableStatusType, { exclude: [EEnableStatusType.All] })}
                        initialState={form.status}
                        handlerFuncion={(event) =>
                          setForm(
                            new TutorialEntity({
                              ...form,
                              status: event.target.selectedIndex as EEnableStatusType,
                            }),
                          )
                        }
                        descriptionKey="interactionDescriptions:pages.adminDashboard.tutorials.adminTutorialPopup.select.select"
                      />

                      <GetSpace left={5} />
                    </>
                  )}

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.adminDashboard.tutorials.adminTutorialPopup.button.onClose"
                  />
                </div>
              }
            />

            <form onSubmit={handleSubmit} className="mt-2 space-y-3">
              <section className="rounded-lg border-2 border-orange-300 p-3">
                <h3 className="text-xs font-black uppercase tracking-wide text-orange-600">Categoria</h3>

                {categories.length > 0 && (
                  <div className="mt-2">
                    <GenericDropDown
                      data={categories.map((item) => ({ label: item, value: item }))}
                      initialState={form.category || categories[0]}
                      handlerFuncion={(event) => setForm(new TutorialEntity({ ...form, category: event.target.value }))}
                      descriptionKey="interactionDescriptions:pages.adminDashboard.tutorials.adminTutorialPopup.select.select2"
                    />
                  </div>
                )}

                <div className="mt-2">
                  <InputField
                    type="text"
                    icon={faFolderOpen}
                    value={form.category}
                    placeholder="Categoria do vídeo"
                    onChange={(e) => setForm(new TutorialEntity({ ...form, category: e.target.value }))}
                    required
                    descriptionKey="interactionDescriptions:pages.adminDashboard.tutorials.adminTutorialPopup.input.categoriaDoVideo"
                  />
                </div>

                {filteredCategories.length > 0 && (
                  <Animate type={AnimationTypes.slideRightIn}>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {filteredCategories.slice(0, 10).map((item) => (
                        <button
                          key={item}
                          type="button"
                          className="rounded-lg bg-orange-300 px-2.5 py-1 text-[11px] font-bold text-gray-800"
                          onClick={() => setForm(new TutorialEntity({ ...form, category: item }))}
                          data-interaction-description-key="interactionDescriptions:pages.adminDashboard.tutorials.adminTutorialPopup.button.setFormNewTutorialEntity"
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </Animate>
                )}
              </section>

              <section className="rounded-xl border border-slate-200 bg-white p-3 grid grid-cols-1 gap-3">
                <div>
                  <p className="mb-2 text-xs font-black uppercase tracking-wide text-orange-600">Público do tutorial</p>

                  <GenericDropDown
                    data={Object.values(ETutorialAudienceType).map((item) => ({
                      label:
                        item === ETutorialAudienceType.Restaurant
                          ? "Restaurante"
                          : item === ETutorialAudienceType.Courier
                            ? "Entregador"
                            : "Ambos",
                      value: item,
                    }))}
                    initialState={form.audience}
                    handlerFuncion={(event) =>
                      setForm(new TutorialEntity({ ...form, audience: event.target.value as ETutorialAudienceType }))
                    }
                    descriptionKey="interactionDescriptions:pages.adminDashboard.tutorials.adminTutorialPopup.select.select3"
                  />
                </div>

                <InputField
                  type="text"
                  icon={faVideo}
                  value={form.title}
                  placeholder="Título do vídeo"
                  onChange={(e) => setForm(new TutorialEntity({ ...form, title: e.target.value }))}
                  required
                  descriptionKey="interactionDescriptions:pages.adminDashboard.tutorials.adminTutorialPopup.input.tituloDoVideo"
                />

                <InputField
                  type="link"
                  icon={faLink}
                  value={form.youtubeUrl}
                  placeholder="URL do YouTube"
                  onChange={(e) => setForm(new TutorialEntity({ ...form, youtubeUrl: e.target.value }))}
                  required
                  descriptionKey="interactionDescriptions:pages.adminDashboard.tutorials.adminTutorialPopup.input.urlDoYouTube"
                />
              </section>

              <div className="pt-1 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <Button
                  label="Salvar"
                  bgColor={THEME.colors.buttonBGSaveColor}
                  typeButton="submit"
                  descriptionKey="interactionDescriptions:pages.adminDashboard.tutorials.adminTutorialPopup.button.salvar"
                />

                {tutorial && (
                  <Button
                    label="Excluir"
                    bgColor={THEME.colors.buttonBGCloseColor}
                    onClick={() =>
                      handleRemoveTutorial(tutorial.id, () => {
                        onSuccess()
                        onClose()
                      })
                    }
                    descriptionKey="interactionDescriptions:pages.adminDashboard.tutorials.adminTutorialPopup.button.excluir"
                  />
                )}
              </div>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
